import React from 'react';
import { useSeo } from '../../hooks/useSeo';
import Header from '../Header';
import Footer from '../Footer';
import { Home } from 'lucide-react';
import { ZODIAC_SIGNS } from './config';

const PERIOD_PILLS = [
    { title: 'Na dziś', slug: 'na-dzis' },
    { title: 'Na jutro', slug: 'na-jutro' },
    { title: 'Dzienny', slug: 'dzienny' },
    { title: 'Tygodniowy', slug: 'tygodniowy' },
    { title: 'Miesięczny', slug: 'miesieczny' },
    { title: 'Roczny', slug: 'roczny' },
];

const HoroscopePage: React.FC = () => {
  useSeo({
    title: `Horoskop dzienny, tygodniowy, miesięczny - esoteric.pl`,
    description: `Sprawdź swój horoskop na dziś, jutro, tydzień, miesiąc lub cały rok. Horoskop miłosny, finansowy i zdrowotny dla Twojego znaku zodiaku.`,
    schema: {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Horoskop", "item": "https://esoteric.pl/horoskop" }
        ]
    }
  });
  
  const currentSign = ZODIAC_SIGNS[4]; // Lew as default
  const currentPeriod = 'ogólny'; // Default period

  return (
    <div className="min-h-screen bg-mystic-bg text-mystic-text flex flex-col">
      <Header />

      <div className="bg-black border-b border-gray-800 py-4">
        <div className="e2e-slider">
          <div className="e2e-track" style={{'--slider-gap': '0.75rem', paddingTop: '0px', paddingBottom: '0px'} as React.CSSProperties}>
            <div className="flex-none w-24 h-24 flex items-center justify-end text-right text-gray-400 font-bold text-sm pr-2">
                <span>Wybierz<br/>swój znak</span>
            </div>
            {ZODIAC_SIGNS.map(sign => (
              <a 
                href={`/horoskop-${sign.slug}`} 
                key={sign.name} 
                className="flex-none w-24 h-24 rounded-xl flex flex-col items-center justify-center text-gray-300 transition-all group bg-mystic-inset border border-gray-800 hover:bg-mystic-accent/10 hover:border-mystic-accent/30"
              >
                <span className="text-3xl">{sign.icon}</span>
                <span className="text-xs font-bold text-white mt-1">{sign.name}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
      
      <div>
        <div className="e2e-slider">
            <div className="e2e-track" style={{'--slider-gap': '0.75rem'} as React.CSSProperties}>
                {PERIOD_PILLS.map((pill) => (
                <a 
                    key={pill.slug}
                    href={`/horoskop-${pill.slug}`}
                    className="flex-none text-center px-6 py-3 rounded-full text-lg font-bold transition-colors bg-mystic-btn text-gray-300 hover:bg-mystic-btnHover"
                >
                    Horoskop {pill.title}
                </a>
                ))}
            </div>
        </div>
      </div>

      <main className="flex-grow py-6">
        <div className="max-w-3xl mx-auto px-4">
          
          <nav aria-label="breadcrumb" className="mb-4">
            <ol className="flex items-center gap-2 text-sm text-gray-400">
                <li>
                    <a href="/" className="hover:text-white transition-colors">
                        <Home className="w-4 h-4" />
                        <span className="sr-only">Home</span>
                    </a>
                </li>
                <li className="text-gray-600">/</li>
                <li>
                    <span className="font-medium text-white">Horoskop</span>
                </li>
            </ol>
          </nav>

          <h1 className="text-4xl md:text-5xl font-heading font-black text-white mb-2 text-left">
            Horoskop
          </h1>
          <p className="text-gray-400 mb-8">
            <strong>Horoskop</strong> to krótka prognoza, która odnosi się do codziennych spraw, emocji i decyzji. Pomaga spojrzeć na bieżące wydarzenia z innej perspektywy i lepiej zrozumieć własne reakcje.
          </p>

          <section className="mx-auto max-w-3xl my-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex -space-x-3">
                <a href="/wrozka-astylla" aria-label="Wróżka Astylla" className="group relative block">
                  <img
                    src="https://assets.esoteric.pl/users/08362923982.jpg"
                    alt="Wróżka Astylla"
                    className="relative h-12 w-12 rounded-full border-2 border-[#111111] object-cover transition duration-300 ease-out group-hover:z-10 group-hover:scale-110 group-hover:brightness-110 group-hover:shadow-lg"
                  />
                </a>

                <a href="/wrozbita-maciej" aria-label="Wróżbita Maciej" className="group relative block">
                  <img
                    src="https://assets.esoteric.pl/users/38573928448.jpg"
                    alt="Wróżbita Maciej"
                    className="relative h-12 w-12 rounded-full border-2 border-[#111111] object-cover transition duration-300 ease-out group-hover:z-10 group-hover:scale-110 group-hover:brightness-110 group-hover:shadow-lg"
                  />
                </a>

                <a href="/wrozka-aida" aria-label="Wróżka Aida" className="group relative block">
                  <img
                    src="https://assets.esoteric.pl/users/20902909333.jpg"
                    alt="Wróżka Aida"
                    className="relative h-12 w-12 rounded-full border-2 border-[#111111] object-cover transition duration-300 ease-out group-hover:z-10 group-hover:scale-110 group-hover:brightness-110 group-hover:shadow-lg"
                  />
                </a>

                <a href="/wroz-maciej" aria-label="Wróż Maciej" className="group relative block">
                  <img
                    src="https://assets.esoteric.pl/users/3339938221.jpg"
                    alt="Wróż Maciej"
                    className="relative h-12 w-12 rounded-full border-2 border-[#111111] object-cover transition duration-300 ease-out group-hover:z-10 group-hover:scale-110 group-hover:brightness-110 group-hover:shadow-lg"
                  />
                </a>

                <a href="/alicja-tarot" aria-label="Alicja Tarot" className="group relative block">
                  <img
                    src="https://assets.esoteric.pl/users/00092383733.jpg"
                    alt="Alicja Tarot"
                    className="relative h-12 w-12 rounded-full border-2 border-[#111111] object-cover transition duration-300 ease-out group-hover:z-10 group-hover:scale-110 group-hover:brightness-110 group-hover:shadow-lg"
                  />
                </a>
              </div>

              <div className="min-w-0">
                <p className="text-sm font-extrabold text-white leading-tight">
                  Horoskopy tworzone z wiedzą i intuicją
                </p>
                <a
                  href="/eksperci"
                  className="inline-flex items-center text-sm font-extrabold text-gray-300 hover:text-white transition"
                >
                  Zobacz naszych ekspertów
                </a>
              </div>
            </div>
          </section>


          <section className="mx-auto max-w-3xl">
            <p className="text-base leading-relaxed text-gray-300 mb-10">
              W tym miejscu znajdziesz horoskopy dla wszystkich znaków zodiaku.
              Codzienne, tygodniowe, miesięczne i roczne. Każdy z nich dotyka innych spraw:
              relacji, pracy, pieniędzy, emocji. Możesz traktować je jak podpowiedź,
              chwilę refleksji albo po prostu ciekawostkę na dobry początek dnia.
            </p>

            <div className="grid gap-4 sm:grid-cols-2 mb-12">
              <a href="/horoskop-na-dzis"
                className="block rounded-2xl border border-gray-800 bg-mystic-inset p-5 hover:border-gray-700 transition">
                <p className="text-sm font-extrabold text-white mb-1">
                  Horoskop na dziś
                </p>
                <p className="text-sm text-gray-400 leading-relaxed">
                  Sprawdź, jaki klimat towarzyszy dzisiejszemu dniu i na co warto zwrócić uwagę.
                </p>
              </a>

              <a href="/horoskop-tygodniowy"
                className="block rounded-2xl border border-gray-800 bg-mystic-inset p-5 hover:border-gray-700 transition">
                <p className="text-sm font-extrabold text-white mb-1">
                  Horoskop tygodniowy
                </p>
                <p className="text-sm text-gray-400 leading-relaxed">
                  Szersza perspektywa na kilka najbliższych dni i zmiany, które mogą się pojawić.
                </p>
              </a>

              <a href="/horoskop-miesieczny"
                className="block rounded-2xl border border-gray-800 bg-mystic-inset p-5 hover:border-gray-700 transition">
                <p className="text-sm font-extrabold text-white mb-1">
                  Horoskop miesięczny
                </p>
                <p className="text-sm text-gray-400 leading-relaxed">
                  Tendencje, które będą wracać przez cały miesiąc. Dobre tło do planów.
                </p>
              </a>

              <a href="/horoskop-roczny"
                className="block rounded-2xl border border-gray-800 bg-mystic-inset p-5 hover:border-gray-700 transition">
                <p className="text-sm font-extrabold text-white mb-1">
                  Horoskop roczny
                </p>
                <p className="text-sm text-gray-400 leading-relaxed">
                  Długofalowy obraz zmian i tematów, które będą miały znaczenie w całym roku.
                </p>
              </a>
            </div>
            <p className="text-base leading-relaxed text-gray-300 mb-8">
              Każdy horoskop podzielony jest na kilka obszarów. Otrzymasz część <span className="font-semibold text-gray-200">ogólną</span>, opis spraw <span className="font-semibold text-rose-200">uczuciowych</span>, wskazówki dotyczące <span className="font-semibold text-emerald-200">pracy i finansów</span> oraz krótkie spojrzenie na <span className="font-semibold text-lime-200">zdrowie i samopoczucie</span>. Dzięki temu możesz czytać tylko te fragmenty, które w danym momencie najbardziej Cię interesują.
            </p>
            <h2 className="text-xl md:text-2xl font-black text-white mb-3">
              Znaki zodiaku i indywidualne prognozy
            </h2>
            <p className="text-base leading-relaxed text-gray-300 mb-8">
              Każdy znak zodiaku reaguje inaczej na te same układy planet.
              Dlatego horoskopy są przygotowywane osobno dla Barana, Byka, Bliźniąt,
              Raka, Lwa, Panny, Wagi, Skorpiona, Strzelca, Koziorożca, Wodnika i Ryb.
              Wybierając swój znak, przechodzisz do prognoz dopasowanych dokładnie do Ciebie.
            </p>

            <h2 className="text-xl md:text-2xl font-black text-white mb-3">
              Jak korzystać z horoskopów
            </h2>
            <p className="text-base leading-relaxed text-gray-300 mb-4">
              Nie trzeba czytać wszystkiego naraz. Czasem wystarczy jeden akapit,
              jedno zdanie, które zostaje w głowie na dłużej.
              Horoskop nie odpowiada na wszystkie pytania,
              ale potrafi pomóc je sobie właściwie zadać.
            </p>

            <p className="text-base leading-relaxed text-gray-300 mb-10">
              Jeśli chcesz sprawdzić, jak prognozy wyglądały wcześniej, możesz też zajrzeć do <a href="/archiwum-horoskopow" className="font-semibold text-white underline underline-offset-4 hover:text-gray-200">archiwum horoskopów</a>. To dobre miejsce, by spojrzeć wstecz i porównać zapowiedzi z tym, co wydarzyło się naprawdę.</p>
          </section>

        </div>
      </main>
      <Footer />
    </div>
  );
};

export default HoroscopePage;